// @ts-strict-ignore
import { SNIPPET_REMOTE_ACTION_MAP_KEY } from 'flow/service/actionOptions';
import { externalSnippetActionTypes } from 'flow/service/snippets/constants';
import { ActionType } from 'flow/store/engine/constants';
import { ContainerType } from 'flow/store/engine/types';
import type {
  Action,
  ContainerContext,
  LegacyActionOption,
  PublicId,
  Rules,
  SnippetContainerContext,
  SnippetRemoteActionMap,
  Threshold,
} from 'flow/store/engine/types';
import { removeGateRuleFromRule } from 'flow/service/rules';
import { generatePublicId } from 'flow/store/engine/utils';


/**
 * Checks if an action is part of a snippet using the transformed
 * action in the redux store.
 */
export function isSnippetChildAction(action: Action): boolean {
  if (!action) return false;
  return action.containerContext?.type === ContainerType.SNIPPET;
}

/**
 * Checks if raw legacy action options contain the snippet remote action map key.
 * Use during forward transformation before containerContext is populated.
 */
export function containsSnippetRemoteActionMap(actionOptions: LegacyActionOption[]): boolean {
  return actionOptions.some(opt => opt.key === SNIPPET_REMOTE_ACTION_MAP_KEY);
}


/**
 * checks if a given action type is external
 */
export function isExternalActionType(actionType: ActionType): boolean {
  // Snippets are neither internal or external
  return externalSnippetActionTypes.includes(actionType);
}


/**
 * In the graph, an internal snippet child is an action that belongs to a snippet
 * and is not an external action type.
 */
export function isInternalSnippetChildAction(action: Action): boolean {
  if (!action) return false;
  return isSnippetChildAction(action) && !isExternalActionType(action.type);
}

/**
 * Checks if the action is a snippet child and is an external type (input/imported).
 */
export function isExternalSnippetChildAction(action: Action): boolean {
  if (!action) return false;
  return isSnippetChildAction(action) && isExternalActionType(action.type);
}

export function supportsActionMerging(action: Action): boolean {
  return isExternalActionType(action.type);
}

/**
 * Checks to see if an action is related by container context to the focused action.
 */
export function isActionRelatedToSnippetAction(args: {
  action: Action;
  focusedAction: Action;
}): boolean {
  const { action, focusedAction } = args;
  if (!isSnippetChildAction(action) || !isSnippetChildAction(focusedAction)) {
    return false;
  }
  const actionKeys = Object.keys(action.containerContext.snippetRemoteActionMap);
  const focusedKeys = new Set(Object.keys(focusedAction.containerContext.snippetRemoteActionMap));
  return actionKeys.some(key => focusedKeys.has(key));
}

/**
 * tests to see if an action is a child of a snippet parent action
 */
export function isChildOfSnippetAction(args: {
  action: Action;
  snippetParentActionPublicId: PublicId;
}) {
  const { action, snippetParentActionPublicId } = args;

  if (!isSnippetChildAction(action)) {
    return false;
  }

  const snippetParentsForAction = Object
    .keys(action.containerContext?.snippetRemoteActionMap ?? {});

  return snippetParentsForAction.includes(snippetParentActionPublicId);
}

export function getRemoteActionPublicId(args: {
  action: Action;
  snippetParentActionPublicId: PublicId;
}) {
  const { action, snippetParentActionPublicId } = args;

  if (!isChildOfSnippetAction({ action, snippetParentActionPublicId })) {
    throw new Error('action is not child of snippet');
  }

  return action.containerContext?.snippetRemoteActionMap[snippetParentActionPublicId];
}


/**
 * Type guard to check if the jsonValue is a valid SnippetRemoteActionMap.
 * @param jsonValue
 * @returns boolean
 */
export function isValidSnippetRemoteActionMap(jsonValue: any): jsonValue is SnippetRemoteActionMap {
  return typeof jsonValue === 'object'
    && jsonValue !== null
    && !Array.isArray(jsonValue);
}

export function createContainerContextForActionFromOptions(args: {
  actionOptions: LegacyActionOption[];
}): ContainerContext {
  const { actionOptions } = args;
  const snippetRemoteActionMapOption = actionOptions
    .find(ao => ao.key === SNIPPET_REMOTE_ACTION_MAP_KEY);

  if (!snippetRemoteActionMapOption) {
    throw new Error('Missing required action option for container context');
  }

  if (!isValidSnippetRemoteActionMap(snippetRemoteActionMapOption.jsonValue)) {
    throw new Error('Invalid jsonValue for snippet remote action map');
  }

  return {
    type: ContainerType.SNIPPET,
    actionOptionPublicId: snippetRemoteActionMapOption.publicId,
    snippetRemoteActionMap: snippetRemoteActionMapOption.jsonValue,
  };
}

export function createSnippetContainerContext(args: {
  actionOptionPublicId?: PublicId;
  parentSnippetActionPublicId: PublicId;
  remoteActionPublicId: PublicId;
}): SnippetContainerContext {
  const { actionOptionPublicId, parentSnippetActionPublicId, remoteActionPublicId } = args;

  return {
    type: ContainerType.SNIPPET,
    actionOptionPublicId: actionOptionPublicId ?? generatePublicId('actionoption'),
    snippetRemoteActionMap: {
      [parentSnippetActionPublicId]: remoteActionPublicId,
    },
  };
}

/** Returns the first parent snippet id */
export function getFirstSnippetParentId(context: SnippetContainerContext) {
  if (context?.snippetRemoteActionMap) {
    return Object.keys(context.snippetRemoteActionMap)[0];
  }
  return null;
}

/** checks if an action requires a gate rule */
export function actionRequiresGateRule(action: Action): boolean {
  // output actions should not have a gate threshold as they do not support rules
  return isInternalSnippetChildAction(action) && action.type !== ActionType.OUTPUT;
}

/** Returns actions that are valid internal snippet children */
export function getActionsRequiringGateRule(actions: Record<string, Action>) {
  return Object.values(actions).filter(actionRequiresGateRule);
}

/** Splits internal and external snippet children */
export function splitSnippetInternalExternalChildren(snippetActions: Action[]): {
  internalSnippetChildren: Action[];
  externalSnippetChildren: Action[];
} {
  const internalSnippetChildren: Action[] = [];
  const externalSnippetChildren: Action[] = [];
  snippetActions.forEach(snippetAction => {
    if (isInternalSnippetChildAction(snippetAction)) internalSnippetChildren.push(snippetAction);
    else externalSnippetChildren.push(snippetAction);
  });
  return { internalSnippetChildren, externalSnippetChildren };
}


export function appendSnippetParent(args: {
  action: Action;
  parentSnippetActionPublicId: PublicId;
  remoteActionPublicId: PublicId;
}): Action {
  const { action, parentSnippetActionPublicId, remoteActionPublicId } = args;

  if (!isSnippetChildAction(action)) {
    const containerContext = createSnippetContainerContext({
      parentSnippetActionPublicId,
      remoteActionPublicId,
    });

    return {
      ...action,
      containerContext,
    };
  }

  // defensive, and mostly a type guard
  if (!action.containerContext) {
    throw new Error('Action should have a container context');
  }

  // return a new action with a new parent snippet & remote action id
  return {
    ...action,
    containerContext: {
      ...action.containerContext,
      snippetRemoteActionMap: {
        ...action.containerContext.snippetRemoteActionMap,
        [parentSnippetActionPublicId]: remoteActionPublicId,
      },
    },
  };
}

/**
 * Returns a new action with the given snippet parent removed from
 * containerContext.snippetRemoteActionMap. If the action has no snippet container
 * context or no entry for the snippet, returns an unchanged clone.
 */
export function removeSnippetParentFromChildContainer(
  action: Action,
  snippetParentPublicId: PublicId,
): Action {
  const snippetRemoteActionMap = action.containerContext?.snippetRemoteActionMap;
  if (!snippetRemoteActionMap || !Object.hasOwn(snippetRemoteActionMap, snippetParentPublicId)) {
    return structuredClone(action);
  }
  const updatedMap = Object.fromEntries(
    Object.entries(snippetRemoteActionMap).filter(([key]) => key !== snippetParentPublicId),
  );
  const updatedAction = structuredClone(action);

  // If there are no remaining snippet parents, remove containerContext
  // Else, update container context to no longer include snippet parent
  if (Object.keys(updatedMap).length === 0) {
    delete updatedAction.containerContext;
  } else {
    updatedAction.containerContext = {
      ...updatedAction.containerContext!,
      snippetRemoteActionMap: updatedMap,
    };
  }
  return updatedAction;
}

export function getGateRuleThresholdId(args: {
  snippetChildActionId: string;
  snippetParentActionId: string | undefined;
  thresholds: Record<string, Threshold> | undefined;
}): string | null {
  const { snippetChildActionId, snippetParentActionId, thresholds } = args;
  if (!snippetParentActionId || !thresholds) return null;
  const match = Object.values(thresholds).find(
    threshold => threshold.actionId === snippetChildActionId
      && threshold.left?.actionId === snippetParentActionId,
  );
  return match?.publicId ?? null;
}

/** Returns rules with the gate rule threshold filtered out so only the snippet's rules are shown */
export function pruneGateRule(args: {
  snippetAction: Action;
  thresholds: Record<string, Threshold> | undefined;
}): Rules | null {
  const { snippetAction, thresholds } = args;
  const { rules } = snippetAction;

  const snippetParentActionId = getFirstSnippetParentId(snippetAction.containerContext);
  const gateRuleThresholdId = getGateRuleThresholdId({
    snippetChildActionId: snippetAction.publicId,
    snippetParentActionId,
    thresholds,
  });
  if (gateRuleThresholdId === null) return rules;
  return removeGateRuleFromRule({ rules, gateRuleThresholdId });
}

/**
 * Returns the public version of the snippet action in the workflow data
 */
export function getSnippetVersionFromWorkflowData(args: {
  actions?: Record<string, Action> | null;
  snippetActionPublicId: PublicId;
}): number | null {
  const { actions, snippetActionPublicId } = args;
  if (!actions) return null;
  const action = actions[snippetActionPublicId];
  return action?.type === ActionType.SNIPPET
    ? action.snippet.publicVersion : null;
}


/**
 * tests if an action is merged onto another. actions are considered merged
 * if both support merging, and they both point to the same remote for the given
 * snippet parent action
 */
export function isSourceActionMergedIntoTargetAction(args: {
  source: Action;
  target: Action;
  snippetParentActionPublicId: PublicId;
}): boolean {
  const { source, target, snippetParentActionPublicId } = args;


  // if target doesn't support merges, it wont contain a merge from source
  if (!supportsActionMerging(target) || !supportsActionMerging(source)) {
    return false;
  }

  // if the target is not a child of the snippet, it wont contain a merge on it
  if (!isChildOfSnippetAction({ action: target, snippetParentActionPublicId })) {
    return false;
  }

  const sourceRemoteId = getRemoteActionPublicId({
    action: source,
    snippetParentActionPublicId,
  });

  const targetRemoteIdAtSnippet = getRemoteActionPublicId({
    action: target,
    snippetParentActionPublicId,
  });


  return sourceRemoteId === targetRemoteIdAtSnippet;
}
